import React, { useState, useEffect } from 'react';
import ProductModal from './ProductModal.jsx';
import { productsAPI } from '../services/api';

const Products = () => {
  const [filters, setFilters] = useState({
    typeOfEquipment: '',
    brand: '',
    manufacturer: '',
    partCategory: '',
    partsName: '',
    partsNumber: ''
  });
  
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);
  

  // Fetch products from API
  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch products using public API (no authentication required)
      const productsResponse = await productsAPI.getAll({ limit: 100 });
      
      setProducts(productsResponse.data.data.products);
      setFilteredProducts(productsResponse.data.data.products);
      
      // Extract unique categories from products
      const uniqueCategories = [...new Set(productsResponse.data.data.products
        .map(product => product.partCategory?.name || product.partCategory)
        .filter(Boolean)
      )].sort();
      
      setCategories(uniqueCategories);
      
    } catch (error) {
      console.error('Failed to fetch data:', error);
      setError('Failed to load data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Filter products based on current filters
  useEffect(() => {
    let filtered = products;

    // Only show active products on public page
    filtered = filtered.filter(product => product.isActive !== false);

    if (filters.typeOfEquipment) {
      filtered = filtered.filter(product => 
        (product.typeOfEquipment?.name || product.typeOfEquipment)?.toLowerCase().includes(filters.typeOfEquipment.toLowerCase())
      );
    }
    if (filters.brand) {
      filtered = filtered.filter(product => 
        (product.brand?.name || product.brand)?.toLowerCase().includes(filters.brand.toLowerCase())
      );
    }
    if (filters.manufacturer) {
      filtered = filtered.filter(product => 
        (product.manufacturer?.name || product.manufacturer)?.toLowerCase().includes(filters.manufacturer.toLowerCase())
      );
    }
    if (filters.partCategory) {
      filtered = filtered.filter(product => 
        (product.partCategory?.name || product.partCategory)?.toLowerCase().includes(filters.partCategory.toLowerCase())
      );
    }
    if (filters.partsName) {
      filtered = filtered.filter(product => 
        product.partsName?.toLowerCase().includes(filters.partsName.toLowerCase())
      );
    }
    if (filters.partsNumber) {
      filtered = filtered.filter(product => 
        product.partsNumber?.toLowerCase().includes(filters.partsNumber.toLowerCase())
      );
    }

    setFilteredProducts(filtered);
  }, [filters, products]);

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const handleCategoryClick = (categoryName) => {
    // Set the category filter
    setFilters(prev => ({
      ...prev,
      partCategory: categoryName
    }));
    
    // Scroll to products section
    document.getElementById('products').scrollIntoView({ behavior: 'smooth' });
  };

  const clearFilters = () => {
    setFilters({
      typeOfEquipment: '',
      brand: '',
      manufacturer: '',
      partCategory: '',
      partsName: '',
      partsNumber: ''
    });
  };

  // Get unique values for filter options
  const getUniqueValues = (field) => {
    const existingValues = [...new Set(products.map(product => {
      const value = product[field];
      return value?.name || value;
    }).filter(Boolean))];
    // Remove duplicates and sort
    return [...new Set(existingValues)].sort();
  };



  const FilterDropdown = ({ field, label, placeholder }) => {
    const uniqueValues = getUniqueValues(field);

    return (
      <div>
        <label className="block text-sm font-medium text-secondary mb-2">
          {label}
        </label>
        <select
          value={filters[field]}
          onChange={(e) => handleFilterChange(field, e.target.value)}
          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
        >
          <option value="">All {label}s</option>
          {uniqueValues.map(value => (
            <option key={value} value={value}>{value}</option>
          ))}
        </select>
      </div>
    );
  };

  const FilterSidebar = () => (
    <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-semibold text-secondary">Filters</h3>
        <button 
          onClick={clearFilters}
          className="text-primary hover:text-opacity-80 text-sm font-medium"
        >
          Clear All
        </button>
      </div>

      <div className="space-y-6">
        {/* Part Category - Dropdown */}
        <FilterDropdown 
          field="partCategory" 
          label="Part Category" 
          placeholder="Select part category..."
        />

        {/* Brand - Dropdown with add option */}
        <FilterDropdown 
          field="brand" 
          label="Brand" 
          placeholder="Select brand..."
        />

        {/* Type of Equipment - Dropdown with add option */}
        <FilterDropdown 
          field="typeOfEquipment" 
          label="Type of Equipment" 
          placeholder="Select equipment type..."
        />

        {/* Manufacturer - Dropdown with add option */}
        <FilterDropdown 
          field="manufacturer" 
          label="Manufacturer" 
          placeholder="Select manufacturer..."
        />

        {/* Parts Name - Text input */}
        <div>
          <label className="block text-sm font-medium text-secondary mb-2">
            Parts Name
          </label>
          <input
            type="text"
            value={filters.partsName}
            onChange={(e) => handleFilterChange('partsName', e.target.value)}
            placeholder="Search parts..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>

        {/* Parts Number - Text input */}
        <div>
          <label className="block text-sm font-medium text-secondary mb-2">
            Parts Number
          </label>
          <input
            type="text"
            value={filters.partsNumber}
            onChange={(e) => handleFilterChange('partsNumber', e.target.value)}
            placeholder="Enter part number..."
            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
          />
        </div>
      </div>
    </div>
  );

  // Loading state
  if (loading) {
    return (
      <section id="products" className="section-padding bg-[#F5F1EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Spare Parts Catalog
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse our comprehensive catalog of heavy truck spare parts. 
              This is a reference catalog for parts identification and information.
            </p>
          </div>
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        </div>
      </section>
    );
  }

  // Error state
  if (error) {
    return (
      <section id="products" className="section-padding bg-[#F5F1EB]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-secondary mb-4">
              Spare Parts Catalog
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Browse our comprehensive catalog of heavy truck spare parts. 
              This is a reference catalog for parts identification and information.
            </p>
          </div>
          <div className="text-center py-12">
            <div className="bg-red-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
              <svg className="w-12 h-12 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 19.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-secondary mb-2">Error Loading Products</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={fetchProducts} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="products" className="section-padding bg-[#F5F1EB]">
      <div className="max-w-7xl mx-auto">
        {/* Our Parts Categories Section */}
        {categories.length > 0 && (
          <div className="mb-16">
            <h3 className="text-2xl font-bold text-secondary text-center mb-8">
              Our Parts Categories
            </h3>
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category, index) => (
                <button
                  key={index}
                  onClick={() => handleCategoryClick(category)}
                  className="bg-primary text-white px-8 py-6 rounded-lg hover:bg-primary/90 transition-colors duration-300 font-medium text-sm w-48 h-20 flex items-center justify-center text-center leading-tight"
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-secondary mb-4">
            Spare Parts Catalog
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Browse our comprehensive catalog of heavy truck spare parts. 
            This is a reference catalog for parts identification and information.
          </p>
        </div>

        {/* Mobile Filter Toggle */}
        <div className="lg:hidden mb-6">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar - Desktop */}
          <div className="hidden lg:block lg:w-1/4">
            <FilterSidebar />
          </div>

          {/* Mobile Filters */}
          {showFilters && (
            <div className="lg:hidden">
              <FilterSidebar />
            </div>
          )}

          {/* Products Grid */}
          <div className="lg:w-3/4">
            {/* Results Count */}
            <div className="mb-6">
              <p className="text-gray-600">
                Showing {filteredProducts.length} of {products.length} products
              </p>
            </div>

            {/* Products Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredProducts.map((product) => (
                <div key={product._id} className="card hover:scale-105 transition-transform duration-300">
                  {/* Product Image with Gallery Indicator */}
                  <div className="mb-4 relative">
                    {/* Main Image */}
                    <img
                      src={product.image || '/clock-with-lamp-it-that-says-word-it.jpg'}
                      alt={product.partsName}
                      className="w-full h-64 object-cover rounded-lg"
                    />
                    
                    {/* Gallery Indicator */}
                    {((product.additionalImages && product.additionalImages.length > 0) || product.image) && (
                      <div className="absolute top-2 right-2 bg-black bg-opacity-60 text-white px-2 py-1 rounded-full text-xs font-medium">
                        {product.image ? 1 + (product.additionalImages ? product.additionalImages.length : 0) : 0} images
                      </div>
                    )}
                  </div>

                  {/* Product Info */}
                  <div className="space-y-3">
                    <h3 className="text-xl font-semibold text-secondary">
                      {product.partsName}
                    </h3>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">Part Number:</span>
                      <span className="font-medium text-primary">{product.partsNumber}</span>
                    </div>

                    <p className="text-gray-600 text-sm leading-relaxed">
                      {product.description || 'No description available'}
                    </p>

                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>{product.brand?.name || product.brand} {product.model}</span>
                      <span>{product.typeOfEquipment?.name || product.typeOfEquipment}</span>
                    </div>

                    {product.partCategory && (
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Category:</span>
                        <span className="font-medium text-primary">{product.partCategory?.name || product.partCategory}</span>
                      </div>
                    )}

                    {/* CTA Button */}
                    <button 
                      onClick={() => {
                        setSelectedProduct(product);
                        setIsModalOpen(true);
                      }}
                      className="btn-primary w-full mt-4"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* No Results */}
            {filteredProducts.length === 0 && products.length > 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-secondary mb-2">No products found</h3>
                <p className="text-gray-600 mb-4">Try adjusting your filters or search terms</p>
                <button onClick={clearFilters} className="btn-primary">
                  Clear Filters
                </button>
              </div>
            )}

            {/* No Products at all */}
            {products.length === 0 && (
              <div className="text-center py-12">
                <div className="bg-gray-100 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4">
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-secondary mb-2">No products available</h3>
                <p className="text-gray-600 mb-4">Check back later for our latest spare parts catalog</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Product Modal */}
      <ProductModal
        product={selectedProduct}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedProduct(null);
        }}
      />
    </section>
  );
};

export default Products;